import { supabase } from "@/lib/supabase";
import { Tables } from "@/types/supabase-types";
import { Job } from "../job-by-name";
import { Item, ItemSelectResult, itemSelect, processItem } from "./items";
import { calculateBaseQuantity, itemUOMSelect, ItemUOMSelectResult } from "./item-uoms";
import { CADCurrencyConverter } from "./currency";

export interface SalesOrder extends Omit<SalesOrderSelectResult, "lines"> {
  allLines: SalesOrderLineItem[];
  jobLines: SalesOrderLineItem[];
}

export interface SalesOrderLineItem extends Omit<SalesOrderLineItemSelectResult, "item"> {
  /** Quantity for the line item converted to the base UOM */
  baseQuantity: number;
  item: Item;
}

interface SalesOrderSelectResult extends Pick<Tables<"SalesOrder">, "id" | "number"> {
  customer: Pick<Tables<"Customer">, "id" | "name">;
  lines: SalesOrderLineItemSelectResult[];
}

interface SalesOrderLineItemSelectResult extends Pick<
  Tables<"SalesOrderLineItem">,
  "id" | "lineNumber" | "description"
> {
  item: ItemSelectResult;
  job: Pick<Tables<"Job">, "id" | "name"> | null;
  /** Quantity for the line item in the selected ItemUOM */
  uomQuantity: number;
  itemUOM: ItemUOMSelectResult | null;
}

const salesOrderSelect = `
  id,
  number,
  customer:Customer!inner(id,name),
  lines:SalesOrderLineItem!inner(
    id,
    lineNumber,
    item:Item!inner(${itemSelect}),
    description,
    uomQuantity:quantity,
    itemUOM:ItemUOM(${itemUOMSelect}),
    job:Job(id, name)
  )
`;

export async function getSalesOrder(job: Job, CADCurrencyConverter: CADCurrencyConverter): Promise<SalesOrder> {
  const salesOrderNumber = job.number;
  const salesOrder = (
    await supabase
      .from("SalesOrder")
      .select<typeof salesOrderSelect, SalesOrderSelectResult>(salesOrderSelect)
      .eq("number", salesOrderNumber)
      .eq("deleted", false)
      .eq("lines.deleted", false)
      .throwOnError()
      .limit(1)
      .maybeSingle()
  ).data;
  if (!salesOrder) throw new Error(`Failed to find a sales order with number ${salesOrderNumber}`);

  return processSalesOrder(salesOrder, job.id, CADCurrencyConverter);
}

function processSalesOrder(
  { lines: rawLines, ...salesOrder }: SalesOrderSelectResult,
  jobId: number,
  CADCurrencyConverter: CADCurrencyConverter,
): SalesOrder {
  const allLines = rawLines
    .map((x) => processSalesOrderLineItem(x, CADCurrencyConverter))
    .sort((a, b) => a.lineNumber - b.lineNumber);
  const jobLines = allLines.filter((x) => x.job?.id === jobId);
  return { ...salesOrder, allLines, jobLines };
}

function processSalesOrderLineItem(
  line: SalesOrderLineItemSelectResult,
  CADCurrencyConverter: CADCurrencyConverter,
): SalesOrderLineItem {
  return {
    ...line,
    baseQuantity: calculateBaseQuantity(line.uomQuantity, line.itemUOM),
    item: processItem(line.item, CADCurrencyConverter),
  };
}
